import { disconnectOdooPortal } from "@/api/odooStorage";

// Customer authentication context — handles signup/login/logout, persists
// JWT token in localStorage, exposes the current customer + apiFetch helper.
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const CustomerAuthContext = createContext(null);
const STORAGE_KEY = "bob.customer.token";

function formatApiDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.detail === "string") return detail.detail;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [customer, setCustomer] = useState(null);
  const [sessionErrorCode, setSessionErrorCode] = useState("");
  const [loading, setLoading] = useState(true);

  const base = process.env.REACT_APP_BACKEND_URL || "";

  const apiFetch = useCallback(async (path, opts = {}) => {
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${base}/api/customer-auth${path}`, { ...opts, headers });
    if (token && (res.status === 401 || res.status === 403)) {
      const payload = await res.clone().json().catch(() => ({}));
      const detail = payload?.detail;
      const code = String(
        payload?.error_code
        || (detail && typeof detail === "object" ? detail.code : "")
        || ""
      ).toLowerCase();
      const blocked = ["account_frozen", "account_deleted", "account_disabled", "account_blocked"].includes(code);
      if (res.status === 401 || blocked) {
        localStorage.removeItem(STORAGE_KEY);
        setSessionErrorCode(code || "session_expired");
        setToken(null);
        setCustomer(null);
      }
    }
    return res;
  }, [base, token]);

  // Public API fetcher (no auth header). Used for /forgot-password etc.
  const apiPublic = useCallback(async (path, opts = {}) => {
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    const res = await fetch(`${base}/api/customer-auth${path}`, { ...opts, headers });
    return res;
  }, [base]);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setCustomer(null); setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${base}/api/customer-auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d = await res.json();
          if (!cancelled) setCustomer(d);
        } else {
          const payload = await res.json().catch(() => ({}));
          const detail = payload?.detail;
          const code = String(
            payload?.error_code
            || (detail && typeof detail === "object" ? detail.code : "")
            || "session_expired"
          ).toLowerCase();
          if (!cancelled) {
            setSessionErrorCode(code);
            setCustomer(null);
            setToken(null);
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch {
        if (!cancelled) setCustomer(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, base]);

  const setSession = useCallback((newToken, newCustomer) => {
    const previousId = customer?.id;
    const nextId = newCustomer?.id;
    if (previousId && nextId && previousId !== nextId) {
      try {
        void disconnectOdooPortal();
      } catch {
        /* ignore */
      }
    }
    localStorage.setItem(STORAGE_KEY, newToken);
    setSessionErrorCode("");
    setToken(newToken);
    setCustomer(newCustomer);
  }, [customer?.id]);

  const logout = useCallback(() => {
    try {
      void disconnectOdooPortal();
    } catch {
      /* ignore */
    }
    localStorage.removeItem(STORAGE_KEY);
    setSessionErrorCode("");
    setToken(null);
    setCustomer(null);
  }, []);

  // Drop stale Odoo portal keys when the BOB session itself is cleared (401 / blocked).
  useEffect(() => {
    if (!token) {
      try {
        void disconnectOdooPortal();
      } catch {
        /* ignore */
      }
    }
  }, [token]);

  const value = { token, customer, loading, sessionErrorCode, apiFetch, apiPublic, setSession, logout, formatApiDetail };
  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
  return ctx;
}
